/**
 * Heights of the chrome docked along the bottom of the viewport, so the floating controls that
 * share that edge stack instead of landing on each other.
 *
 * Each piece of docked chrome is conditional — logged-out visitors, mobile, and routes that opt
 * out have no chat dock; only detail pages raise the Magic Island; only ArchiPro staff on the
 * revamp get the feedback button. Rather than have every floating control re-derive those
 * conditions, whatever owns a piece publishes its height on the root element while it is on
 * screen, and the controls that must clear it read the variable. Unset means that piece is
 * absent and contributes nothing, so consumers default it to `0px`.
 *
 * The bottom-right corner reads, from the viewport floor up: the chat dock, the feedback button,
 * then Back to top. The Magic Island is centred rather than cornered, so it only enters the sum
 * below `md` — see {@link FLOATING_ISLAND_HEIGHT_VAR}.
 */
export const FLOATING_DOCK_HEIGHT_VAR = '--floating-dock-height';

/**
 * Space the Magic Island occupies, published only below `md`.
 *
 * The island is centred, so at desktop widths it is nowhere near the cornered controls and adding
 * it to their offset would only push them up for no reason. On a phone the pill is wide enough
 * relative to the viewport that a bottom-right control would sit on top of it, so there it counts.
 *
 * Unlike the chat dock this one does **not** rest on the viewport floor — it floats a berth above
 * it, plus the iOS home-indicator inset. So what it publishes is its top edge's distance from the
 * floor, not its own box height; see {@link floatingSpaceAbove}.
 */
export const FLOATING_ISLAND_HEIGHT_VAR = '--floating-island-height';

/**
 * Space the sitewide feedback button occupies, **including the gap above it**.
 *
 * It is the one piece of this stack with another control resting on it, and a CSS variable that
 * is simply unset cannot also drop a gap that was added beside it. Publishing the gap as part of
 * the measurement keeps the absent case exact: with no feedback button, Back to top sits where it
 * always did rather than floating a gap higher.
 */
export const FLOATING_FEEDBACK_HEIGHT_VAR = '--floating-feedback-height';

/** Gap between the viewport floor (or the chrome above it) and floating controls. */
export const FLOATING_DOCK_GAP_REM = 1.25;

/**
 * How much of the bottom edge a piece of chrome takes up, measured from the viewport floor.
 *
 * Every variable here answers "how high is the first free pixel", not "how tall is that element".
 * The two are the same only for chrome that rests on the floor, which is why the chat dock can
 * publish its bare height. Anything holding itself off the floor has to add that berth, or a
 * control stacking on it is told the top edge is lower than it is and lands on top of it.
 *
 * @param bottomOffsetPx Distance from the viewport floor to the element's bottom edge
 * @param heightPx       The element's own laid-out height
 */
export const floatingSpaceAbove = (
    bottomOffsetPx: number,
    heightPx: number,
): string => `${bottomOffsetPx + heightPx}px`;

/** Sum published heights into a `bottom` value, with one gap above the topmost of them. */
const stackAbove = (vars: string[]) => {
    const heights = vars.map((name) => `var(${name}, 0px)`).join(' + ');
    return `calc(${heights} + ${FLOATING_DOCK_GAP_REM}rem)`;
};

/**
 * `bottom` for the sitewide feedback button: the lowest of the cornered controls, clearing the
 * chat dock, and the Magic Island on the widths where the two would meet.
 */
export const floatingFeedbackOffset = () =>
    stackAbove([FLOATING_DOCK_HEIGHT_VAR, FLOATING_ISLAND_HEIGHT_VAR]);

/**
 * `bottom` for a floating control that must stack above everything else in the corner — today
 * Back to top, which rides above the feedback button when staff have one.
 */
export const floatingDockOffset = () =>
    stackAbove([
        FLOATING_DOCK_HEIGHT_VAR,
        FLOATING_ISLAND_HEIGHT_VAR,
        FLOATING_FEEDBACK_HEIGHT_VAR,
    ]);
